import styled from "styled-components";
import { device } from "../../../utils/device";
// Single design-system card radius (radius.md = 8px) — shared with the Profile
// and Primary-Wheel gallery cards so all three cards' corners are identical.
import { radius } from "../../../theme/tokens";
import { accentBorderSx } from "../../Theming/themeEngine";

// QA-149 (Kelly, 2026-08-19): the gallery header's SearchBar is asked to
// align its right edge with the business box's own right edge. Exported so
// SearchBar/styles.js can size its max-width off this SAME literal instead
// of an independent magic number — the two can drift in pixel value but
// never in intent, and a future resize of the business box only needs to
// change this one constant.
export const BUSINESS_BOX_WIDTH_PX = 350;

// ISP-9999 / QA-149 #2 (Kelly/Gordon, 2026-08-20): the gallery sidebar's own
// left margin (getGallerySidebarSx, pages/Gallery/galleryResponsiveLayout.js)
// ahead of this box, in desktop (non-narrow) layout. Exported from here (not
// re-declared in the page file) so BOTH the sidebar's own inset AND
// SearchBar/styles.js's right-edge math read the SAME literal — the earlier
// QA-149 fix only matched WIDTHS (BUSINESS_BOX_WIDTH_PX), which cannot align
// right edges on its own when the two boxes start at different left insets.
export const BUSINESS_BOX_LEFT_INSET_PX = 10;

// M390-04 (mobile-390 sweep vY, MEASURED on staging bundle main.708ccddf.js,
// /gallery/21 @390x844, authed, own gallery): the AYMO promo card's
// mobile-portrait max-height (previously `calc(100svh - 18svh - 6vw)` — and a
// second, drifted `calc(100svh - 15svh - 6vw)` copy for the isMobilePortrait
// JS-prop path below) resolved to ~669px on an 844px-tall viewport: the card
// alone filled 79% of the FIRST viewport, pushing the profile header, the
// Created/Cloned/Unlocked/Favorited tab bar and the whole wheel grid below the
// fold with a big empty grey gap visible while data streamed in. The gallery's
// isNarrow layout (galleryResponsiveLayout.js) stacks this card ABOVE that
// content in a single column, so the card's own height is exactly the budget
// everything else has to work with. Shrinking the subtracted band from
// 15-18svh to 55svh caps the card at roughly 45% of the viewport instead of
// ~80%, leaving real room for the header + tabs on a phone screen; content
// taller than that already scrolls inside the card (`overflow: scroll` below,
// unaffected by this change) rather than being clipped.
export const MOBILE_PORTRAIT_MAX_HEIGHT = "calc(100svh - 55svh - 6vw)";

// T22.209 (UX audit UX-09, Gordon 2026-10-13, MEASURED live web-stg
// main.dd06f65e.js /gallery/<own id> @390x844): the GALLERY card capped at
// the height above was 356px tall holding 549px of content behind its own
// little scrollbar, the AYMO copy cut off mid-line ("...and get") and the
// "Set Up Business Box" CTA hidden below the card's fold. On the Gallery the
// card already sits in galleryResponsiveLayout.js's isNarrow single-column
// stack, where <main> owns the scroll and the sidebar row is uncapped
// (maxHeight:"none", overflow:"visible") -- the cap only adds a nested scroll
// trap. So the Gallery card (isGalleryBbox) now grows to its content on a
// phone; every other ViewOne (Builder pane / previews) keeps the cap above.
export const mobilePortraitSizing = ({ isFormPreview, isGalleryBbox }) =>
  isFormPreview || isGalleryBbox
    ? { maxHeight: "none", overflow: "visible" }
    : { maxHeight: MOBILE_PORTRAIT_MAX_HEIGHT, overflow: "scroll" };

// fix-stg0919-gallery-profile-menu-covered-390: this card's own mobile-portrait
// stacking context (below). Exported so galleryResponsiveLayout.js's header
// slot can pin its own z-index ABOVE this one by construction instead of an
// independent literal that can silently drift out of order again (see that
// file's getGalleryHeaderSlotSx for the header side of this pairing).
export const MOBILE_PORTRAIT_CARD_Z_INDEX = 200;

export const ViewOne = styled.div`
  /* hw-0914-staging-business-box-copy-clipped (2026-09-14): was a fixed
     ${BUSINESS_BOX_WIDTH_PX}px, wider than the Wheel/Builder side pane below
     ~1400px viewports (pane = 25vw). The card no longer overflowed its
     ancestor after BUG-606a/QA-1120 added overflow:hidden to that ancestor,
     but the excess was simply CLIPPED, not reflowed — the promo copy (word-
     wrap: break-word below, already correct) cut off mid-word at the pane's
     edge instead of wrapping into a narrower column. width: 100% (capped at
     the same ${BUSINESS_BOX_WIDTH_PX}px ceiling via max-width) lets this
     shrink to whatever the pane actually has, so the text wraps inside it
     instead of being cut off. */
  width: 100%;
  max-width: ${BUSINESS_BOX_WIDTH_PX}px;
  margin-right: auto;
  margin-left: auto;
  min-width: 100px;
  max-height: ${(props) => (props.isFormPreview ? "none" : "90vh")};
  background: ${(props) =>
    props.isDefault
      ? `linear-gradient(
      144.96deg,
      #ffffff 0%,
      #ffffff 35%,
      rgba(255, 255, 255, 0.9) 45%,
      rgba(204, 254, 255, 0.7) 55%,
      rgba(247, 254, 213, 0.7) 70%,
      rgba(253, 207, 254, 0.7) 85%,
      rgba(245, 238, 222, 0.8) 100%
    ),
    linear-gradient(0deg, rgba(255, 255, 255, 0.4) 0%, #ffffff 100%)`
      : "#ffffff"};
  padding: 24px;
  /* ISP-9999 (fix-card-corners-token-remaining-cards-after-5572): matches the
     Profile + Primary-Wheel gallery cards (WheelListing/index.js,
     PrimaryWheel/style.js, #5572) -- reads the Gallery Theme studio's saved
     Card Corners setting via the same CSS var, falling back to the unchanged
     radius.md token. Was hardcoded to radius.md only, so a non-default Card
     Corners choice never reached this card while its siblings followed it. */
  border-radius: var(--gt-card-radius, ${radius.md}px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: ${(props) => (props.isFormPreview ? "visible" : "auto")};
  gap: 10px;
  /* gordon-0918b-gallery-accent-border-all-four-spaces (2026-09-18, GORDON
     REPEAT REPORT): the Business Box -- both the "iYuga Business Box!"
     upsell variant (GuestWithBoxPromo below) and this real-box variant --
     was still a hardcoded "1px solid #BBBBBB", one of the four named spaces
     the accent never reached. Shared accentBorderSx (Theming/themeEngine.js)
     so it can't drift from the Profile / Featured Wheel / Wheel Grid /
     wheel-grid card borders. Only rendered on the Gallery page
     (isGalleryBbox), matching this rule's existing gate. */
  border: ${(props) => props.isGalleryBbox && accentBorderSx().border};
  position: relative; /* Ensure parent is relative for absolute positioning */

  /* Default business box should fill available height */
  ${(props) =>
    props.isDefault &&
    props.isGalleryBbox &&
    `
    height: 98%;
    justify-content: space-between;
  `}

  /* fix-vy0918-business-box-card-still-undersized-354-vs-549: this card's
     mobile-portrait height is intentionally capped below its own content
     height (M390-04 above), so it's expected to scroll — hiding the
     scrollbar left ZERO on-screen signal of that, so the card looked
     finished/broken (copy cut off mid-sentence, CTA button unreachable-
     looking) instead of scrollable. Same bug shape + fix as
     BrowseWheels.scrollbarVisible.test.jsx
     (hw-0914-dev-wheel-selection-list-hides-its-scrollbar): stop hiding the
     scrollbar instead of growing the card (which would reintroduce M390-04). */

  /* Form preview specific styles */
  ${(props) =>
    props.isFormPreview &&
    `
    position: relative;
  `}

  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  /* T22.635 (Gordon S23+, web-stg /wheel/510012): 94vw is wider than the
     phone View Wheel Business Box sheet's padded content box below ~373px
     (338px card in a 328px box at 360px), so the card's right edge was cut
     and the sheet scrolled sideways. Never wider than the parent. */
  @media (${device.mobilePortrait}) {
    width: 94vw;
    max-width: min(${BUSINESS_BOX_WIDTH_PX}px, 100%);
    position: relative;
    right: auto;
    left: auto;
    top: auto;
    background: #e3efeb;
    z-index: ${MOBILE_PORTRAIT_CARD_Z_INDEX};
    max-height: ${(props) => mobilePortraitSizing(props).maxHeight};
    overflow: ${(props) => mobilePortraitSizing(props).overflow};

    p {
      text-align: center !important;
    }

    img {
      width: 100% !important;
    }
  }

  ${({ isMobilePortrait, isFormPreview, isGalleryBbox }) =>
    isMobilePortrait &&
    `
    width: 94vw;
    max-width: min(${BUSINESS_BOX_WIDTH_PX}px, 100%);
    position: relative;
    right: auto;
    left: auto;
    top: auto;
    background: #e3efeb;
    z-index: ${MOBILE_PORTRAIT_CARD_Z_INDEX};
    max-height: ${mobilePortraitSizing({ isFormPreview, isGalleryBbox }).maxHeight};
    overflow: ${mobilePortraitSizing({ isFormPreview, isGalleryBbox }).overflow};

    p {
      text-align: center !important;
    }

    img {
      width: 100% !important;
    }`}
`;

export const IconContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: ${(props) => (props.isDefault ? "35px" : "25px")};
  margin: ${(props) => (props.isDefault ? "30px auto 0" : "10px auto 0")};
  max-width: fit-content;
`;

export const IconWrapper = styled.div`
  cursor: pointer;
`;
